import React from 'react';
import { Download, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { personalInfo, skills, experience, education, projects } from '../data/mock';

const HIGHLIGHT_IDS = [
  'denial-review-workbench',
  'vifei-suite',
  'agent-cassette',
  'flow-profile',
  'aliveca',
  'mission-relay',
];

const HIGHLIGHT_BLURBS = {
  'denial-review-workbench': 'Auditable healthcare denial-review workflow with human approval, escalation guards, append-only logs, deterministic replay, and Vifei export.',
  'vifei-suite': 'Local-first Rust evidence suite for AI-agent runs, deterministic replay, Truth HUDs, incident packs, and fail-closed export.',
  'agent-cassette': 'TypeScript record/replay capture layer for tool calls, model responses, and side-effect gates.',
  'flow-profile': 'Auditable context bundle generator for AI assistants and support workflows, with redaction, SARIF risk detection, token budgeting, and AI-ready artifacts.',
  'aliveca': 'Local-first full-stack PWA and compiler for arena-scale LED Mapping Truth Packs, with validation, QR test patterns, verification reports, and cross-platform exports.',
  'mission-relay': 'Mobile-first creator mission platform with missions, community wall, live circles, creator/fan roles, and engagement unlocks.',
};

const highlights = HIGHLIGHT_IDS
  .map(id => projects.find(p => p.id === id))
  .filter(Boolean);

const About = () => {
  return (
    <main id="main-content" className="pt-24 sm:pt-32 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Intro Section */}
        <section className="max-w-4xl mx-auto mb-20">
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
                About Me
              </h1>
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  {personalInfo.location} • {personalInfo.remote}
                </div>
              </div>
            </div>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {personalInfo.bio}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <a href="mailto:eucapop@gmail.com?subject=Resume%20Request%20-%20Yuan%20Liu">
                  <Download className="mr-2 h-4 w-4" />
                  Request Resume
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <a href="/contact">Get in Touch</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Project Highlights Section */}
        <section className="max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl font-bold tracking-tight mb-3">Project Highlights</h2>
          <p className="text-muted-foreground mb-8 max-w-[68ch] leading-[1.6]">
            A few current projects across auditable AI workflows, mobile products,
            developer tooling, and real-time creative infrastructure.
          </p>

          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {highlights.map((p) => {
              const href = p.link && p.link.startsWith('http') ? p.link : (p.github || null);
              const isExternal = href && href.startsWith('http');

              const Inner = (
                <>
                  <p className="text-xs font-medium text-muted-foreground tracking-wider uppercase mb-1">
                    {p.category}
                  </p>
                  <h3 className="text-base font-semibold leading-snug group-hover:text-accent transition-colors duration-[var(--dur-2)]">
                    {p.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-[1.55]">
                    {HIGHLIGHT_BLURBS[p.id]}
                  </p>
                  {p.metrics && (
                    <p className="text-xs text-accent font-medium mt-3">{p.metrics}</p>
                  )}
                </>
              );

              return (
                <li key={p.id}>
                  {href ? (
                    <a
                      href={href}
                      target={isExternal ? '_blank' : undefined}
                      rel={isExternal ? 'noopener noreferrer' : undefined}
                      className="block h-full p-4 rounded-[var(--r2)] border border-border bg-card hover:border-accent/40 transition-colors duration-[var(--dur-2)] group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    >
                      {Inner}
                    </a>
                  ) : (
                    <div className="block h-full p-4 rounded-[var(--r2)] border border-border bg-card group">
                      {Inner}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        {/* Experience Section */}
        <section className="max-w-4xl mx-auto mb-20">
          <h2 className="text-3xl font-bold tracking-tight mb-8">Experience</h2>
          <div className="space-y-8">
            {experience.map((exp, idx) => (
              <Card key={idx}>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <CardTitle className="text-xl">{exp.role}</CardTitle>
                      <p className="text-muted-foreground mt-1">{exp.company}</p>
                    </div>
                    <Badge variant="outline" className="w-fit">{exp.period}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {exp.highlights.map((highlight, hIdx) => (
                      <li key={hIdx} className="text-sm text-muted-foreground flex gap-3">
                        <span className="text-primary mt-1">•</span>
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Skills Section */}
        <section className="max-w-4xl mx-auto mb-20">
          <h2 className="text-3xl font-bold tracking-tight mb-8">Technical Skills</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Languages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {skills.languages.map((skill, idx) => (
                    <Badge key={idx} variant="secondary">{skill}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Frontend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {skills.frontend.map((skill, idx) => (
                    <Badge key={idx} variant="secondary">{skill}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Backend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {skills.backend.map((skill, idx) => (
                    <Badge key={idx} variant="secondary">{skill}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Databases</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {skills.databases.map((skill, idx) => (
                    <Badge key={idx} variant="secondary">{skill}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">DevOps & Cloud</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {skills.devops.map((skill, idx) => (
                    <Badge key={idx} variant="secondary">{skill}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Graphics & Media</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {skills.graphics.map((skill, idx) => (
                    <Badge key={idx} variant="secondary">{skill}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Education Section */}
        <section className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold tracking-tight mb-8">Education</h2>
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">{education.degree}</CardTitle>
              <p className="text-muted-foreground">{education.institution}</p>
            </CardHeader>
            <CardContent>
              <Separator className="mb-4" />
              <div>
                <h4 className="font-semibold mb-2">Certifications</h4>
                <ul className="space-y-1">
                  {education.certifications.map((cert, idx) => (
                    <li key={idx} className="text-sm text-muted-foreground flex gap-2">
                      <span className="text-primary">•</span>
                      {cert}
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </main>
  );
};

export default About;
